import React, { useEffect, useMemo } from 'react';

type Post = {
  title: string;
  slug: string;
  description: string;
  content: string;
};

const modules = import.meta.glob('../../content/blog/*.md', { as: 'raw', eager: true });

const parseFrontmatter = (raw: string): Post => {
  const match = raw.match(/^---\n([\s\S]*?)\n---\n([\s\S]*)$/);
  const frontmatter = match?.[1] || '';
  const body = match?.[2] || raw;
  const fields = Object.fromEntries(
    frontmatter
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const [key, ...rest] = line.split(':');
        return [key.trim(), rest.join(':').trim()];
      })
  );

  return {
    title: (fields.title as string) || 'Untitled',
    slug: (fields.slug as string) || '',
    description: (fields.description as string) || '',
    content: body.trim()
  };
};

const posts: Post[] = Object.values(modules)
  .map((raw) => parseFrontmatter(raw as string))
  .filter((post) => post.slug);

const renderMarkdownLite = (md: string) => {
  // Minimal markdown rendering for headings and lists to keep bundle light.
  const html = md
    .replace(/^### (.*)$/gm, '<h3>$1</h3>')
    .replace(/^## (.*)$/gm, '<h2>$1</h2>')
    .replace(/^# (.*)$/gm, '<h1>$1</h1>')
    .replace(/^\- (.*)$/gm, '<li>$1</li>')
    .replace(/<\/li>\n<li>/g, '</li><li>')
    .replace(/(<li>.*<\/li>)/gs, '<ul>$1</ul>')
    .replace(/\n{2,}/g, '</p><p>')
    .replace(/\n/g, '<br />');

  return { __html: `<p>${html}</p>` };
};

export const BlogPage = () => {
  const pathname = useMemo(() => (typeof window !== 'undefined' ? window.location.pathname : '/blog'), []);
  const slug = pathname.startsWith('/blog/') ? pathname.replace('/blog/', '').replace(/\/$/, '') : '';
  const post = posts.find((item) => item.slug === slug);
  const isDetail = Boolean(post);
  const canonicalUrl = isDetail ? `https://www.e-moto.io/blog/${post?.slug}` : 'https://www.e-moto.io/blog';

  useEffect(() => {
    let canonicalTag = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonicalTag) {
      canonicalTag = document.createElement('link');
      canonicalTag.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalTag);
    }
    canonicalTag.setAttribute('href', canonicalUrl);

    let ogUrl = document.querySelector<HTMLMetaElement>('meta[property="og:url"]');
    if (!ogUrl) {
      ogUrl = document.createElement('meta');
      ogUrl.setAttribute('property', 'og:url');
      document.head.appendChild(ogUrl);
    }
    ogUrl.setAttribute('content', canonicalUrl);
  }, [canonicalUrl]);

  const blogJsonLd = {
    '@context': 'https://schema.org',
    '@type': isDetail ? 'BlogPosting' : 'Blog',
    name: isDetail ? post?.title : 'E-Moto Blog',
    url: isDetail ? `https://www.e-moto.io/blog/${post?.slug}` : 'https://www.e-moto.io/blog',
    description: isDetail ? post?.description : 'Guides on buying, selling, and staying safe with cars in South Africa, powered by on-the-ground agents.',
    headline: isDetail ? post?.title : undefined,
    articleBody: isDetail ? post?.content : undefined,
    blogPost: isDetail
      ? undefined
      : posts.map((p) => ({
          '@type': 'BlogPosting',
          headline: p.title,
          url: `https://www.e-moto.io/blog/${p.slug}`,
          description: p.description
        }))
  };

  if (isDetail && post) {
    return (
      <div className="min-h-screen bg-brand-surface px-6 py-12 text-brand-text">
        <div className="mx-auto max-w-4xl space-y-6">
          <a href="/blog" className="text-sm font-semibold text-brand-dark hover:underline">
            ← Back to blog
          </a>
          <article className="space-y-4 rounded-3xl border border-[#DFEDEC] bg-white/95 p-6 shadow-sm shadow-brand/10">
            <p className="text-xs font-semibold uppercase tracking-[0.35em] text-brand-dark">Blog</p>
            <h1 className="font-display text-3xl font-semibold leading-tight text-brand-text">{post.title}</h1>
            <p className="text-brand-subtext">{post.description}</p>
            <div className="prose prose-slate max-w-none text-brand-text [&_*]:break-words" dangerouslySetInnerHTML={renderMarkdownLite(post.content)} />
          </article>
        </div>
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(blogJsonLd) }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-surface px-6 py-12 text-brand-text">
      <div className="mx-auto max-w-5xl space-y-10">
        <header className="space-y-3">
          <p className="text-xs font-semibold uppercase tracking-[0.35em] text-brand-dark">Blog</p>
          <h1 className="font-display text-3xl font-semibold leading-tight">Guides to buy, sell, and stay safe with cars in SA</h1>
          <p className="text-brand-subtext">Deep dives written for South African drivers—inspections, paperwork, pricing, safety, and agent-led tips.</p>
        </header>

        <section className="grid gap-6 md:grid-cols-2">
          {posts.map((postItem) => (
            <article key={postItem.slug} className="flex h-full flex-col rounded-2xl border border-[#DFEDEC] bg-white/90 p-6 shadow-sm shadow-brand/10">
              <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-brand-dark break-words">Guide</p>
              <h2 className="font-display mt-2 text-xl font-semibold text-brand-text break-words">{postItem.title}</h2>
              <p className="mt-2 text-sm text-brand-subtext">{postItem.description}</p>
              <p className="mt-3 text-xs font-medium text-brand-dark/70">/blog/{postItem.slug}</p>
              <div className="mt-4 flex flex-wrap gap-3">
                <a
                  href={`/blog/${postItem.slug}`}
                  className="rounded-full bg-brand-dark px-4 py-2 text-xs font-semibold uppercase tracking-[0.24em] text-white shadow-sm shadow-brand/20 transition hover:bg-brand-dark/95"
                >
                  Read post
                </a>
              </div>
            </article>
          ))}
        </section>
      </div>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(blogJsonLd) }} />
    </div>
  );
};
